import { State } from '@progress/kendo-data-query';
import { FC, ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import useAuth from 'src/hooks/useAuth';
import useCompany from 'src/hooks/useCompany';
import useRedirect from 'src/hooks/useRedirect';
import useAttachDocumentApi from 'src/services/useAttachDocumentApi';
import useAttachToReceiptOrInvoiceApi from 'src/services/useAttachToReceiptOrInvoiceApi';
import useCreateInvoiceApi from 'src/services/useCreateInvoiceApi';
import useCreateReceiptApi from 'src/services/useCreateReceiptApi';
import { IAttachDocument } from 'src/types/AttachDocument';
import { SetState } from 'src/types/generic';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { initialScanManagerDataState } from './AttachDocumentWizard';

const dateArr = ['creationDate'];
interface AttachDocumentContextProps {
  dataState: State;
  setDataState: SetState<State>;
  isLoading: boolean;
  attachmentListData: IAttachDocument[];
  setAttachmentListData: SetState<IAttachDocument[]>;
  count: number;
  getAttachDocuments: () => void;
  handleAttachment: (useSinglePeriod: boolean) => void;
  handleWithoutAttachment: (useSinglePeriod: boolean) => void;
  isRowSelected: boolean;
  isInvoice: boolean;
  selectedAttachments: number[];
  setSelectedAttachments: SetState<number[]>;
  isExistingDocument: boolean;
  isAttachmentButtonLoading: boolean;
}

export const AttachDocumentContext = createContext<AttachDocumentContextProps>(
  {} as AttachDocumentContextProps
);

interface ContextProviderProps {
  children: ReactNode;
  isInvoice: boolean;
  poId?: number;
  supplierId?: number;
  receiptId?: number;
  invoiceNumber?: string;
  refresh?: () => void;
  isEnterReceipts: boolean;
  isNewTab: boolean;
  invoiceCompanyId?: string;
}
const AttachDocumentContextProvider: FC<ContextProviderProps> = ({
  children,
  isInvoice,
  poId,
  supplierId,
  receiptId,
  invoiceNumber,
  refresh,
  isNewTab,
  isEnterReceipts,
  invoiceCompanyId,
}) => {
  const { getUrl, redirect } = useRedirect();
  const { attachDocument, isAttachDocumentLoading } = useAttachDocumentApi(isInvoice);
  const {
    attachDocumentsToReceiptOrInvoice,
    isAttachDocumentLoading: isAttachDocumentsToReceiptOrInvoiceLoading,
  } = useAttachToReceiptOrInvoiceApi(isInvoice);
  const { createInvoice, isCreateInvoiceLoading } = useCreateInvoiceApi();
  const { createReceipt, isCreateReceiptLoading } = useCreateReceiptApi();

  const { company } = useCompany();
  const { user } = useAuth();
  const companyId = useMemo(
    () => company.companyId || user.CompanyId,
    [company.companyId, user.CompanyId]
  );
  const [dataState, setDataState] = useState(initialScanManagerDataState(isInvoice));
  const [attachmentListData, setAttachmentListData] = useState<IAttachDocument[]>([]);
  const [count, setCount] = useState(0);
  const [selectedAttachments, setSelectedAttachments] = useState<number[]>([]);

  useEffect(() => {
    setDataState(initialScanManagerDataState(isInvoice));
  }, [isInvoice, setDataState]);

  const isAttachmentButtonLoading = useMemo(
    () =>
      isAttachDocumentsToReceiptOrInvoiceLoading ||
      isCreateInvoiceLoading ||
      isCreateReceiptLoading,
    [isAttachDocumentsToReceiptOrInvoiceLoading, isCreateInvoiceLoading, isCreateReceiptLoading]
  );

  const selectedRow = useMemo(
    () => attachmentListData.filter((item) => item?.selected === true),
    [attachmentListData]
  );

  const attachmentIdList = useMemo(() => {
    const data: number[] = [];
    selectedRow.forEach((item) => {
      data.push(item.attachId);
    });
    return data;
  }, [selectedRow]);

  const getAttachDocuments = useCallback(() => {
    const requestBody = modifyRequest(dataState, dateArr);
    attachDocument({ companyId: companyId, body: requestBody }).then((res) => {
      if (res?.items) {
        setAttachmentListData(
          res?.items?.map((item) => {
            if (selectedAttachments?.includes(item?.attachId)) {
              return { ...item, selected: true };
            }
            return item;
          })
        );
        setCount(res.totalRecords);
      }
    });
  }, [dataState, attachDocument, companyId, selectedAttachments]);

  const redirectToInvoiceOrReceiptDetails = useCallback(
    (receiptId: number, newPoId?: number) => {
      if (isInvoice) {
        redirect(
          getUrl('invoiceDetails')(String(receiptId), String(poId || newPoId)),
          false,
          isNewTab
        );
      } else {
        redirect(
          getUrl('receiptDetails')(String(receiptId), String(poId || newPoId), isEnterReceipts),
          false,
          isNewTab
        );
      }
    },
    [getUrl, isEnterReceipts, isInvoice, isNewTab, poId, redirect]
  );

  const handleReceiptWithoutAttachment = useCallback(() => {
    if (receiptId) {
      redirectToInvoiceOrReceiptDetails(receiptId);
    } else {
      createReceipt({
        companyId: invoiceCompanyId || companyId,
        poId: poId,
        supplierId: supplierId,
        attachmentIds: attachmentIdList,
      })
        .then((data) => {
          if (!data) return;
          redirectToInvoiceOrReceiptDetails(data.receiptId, data.poId);
        })
        .catch(console.error)
        .finally(() => {
          getAttachDocuments();
          refresh?.();
        });
    }
  }, [
    receiptId,
    redirectToInvoiceOrReceiptDetails,
    createReceipt,
    invoiceCompanyId,
    companyId,
    poId,
    supplierId,
    attachmentIdList,
    getAttachDocuments,
    refresh,
  ]);

  const handleInvoiceWithoutAttachment = useCallback(
    (useSinglePeriod: boolean) => {
      if (receiptId) {
        redirectToInvoiceOrReceiptDetails(receiptId);
      } else {
        createInvoice({
          companyId: invoiceCompanyId || companyId,
          poId,
          invoiceNumber,
          attachmentIds: attachmentIdList,
          supplierId,
          useSinglePeriod: useSinglePeriod,
        })
          .then((data) => {
            if (!data) return;
            redirectToInvoiceOrReceiptDetails(data.invoiceId, data.poId);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            getAttachDocuments();
            refresh?.();
          });
      }
    },
    [
      receiptId,
      redirectToInvoiceOrReceiptDetails,
      createInvoice,
      invoiceCompanyId,
      companyId,
      poId,
      invoiceNumber,
      attachmentIdList,
      supplierId,
      getAttachDocuments,
      refresh,
    ]
  );

  const handleReceiptOrInvoiceAttachment = useCallback(
    (useSinglePeriod: boolean) => {
      if (receiptId) {
        attachDocumentsToReceiptOrInvoice({
          id: receiptId,
          attachIds: attachmentIdList,
        })
          .then((data) => {
            if (!data) return;
            if (receiptId) redirectToInvoiceOrReceiptDetails(receiptId);
          })
          .catch(console.error)
          .finally(() => {
            getAttachDocuments();
            refresh?.();
          });
      } else {
        if (isInvoice) {
          handleInvoiceWithoutAttachment(useSinglePeriod);
        } else {
          handleReceiptWithoutAttachment();
        }
      }
    },
    [
      attachDocumentsToReceiptOrInvoice,
      attachmentIdList,
      getAttachDocuments,
      handleInvoiceWithoutAttachment,
      handleReceiptWithoutAttachment,
      isInvoice,
      receiptId,
      redirectToInvoiceOrReceiptDetails,
      refresh,
    ]
  );

  const handleWithoutAttachment = useCallback(
    (useSinglePeriod: boolean) => {
      if (isInvoice) {
        handleInvoiceWithoutAttachment(useSinglePeriod);
      } else {
        handleReceiptWithoutAttachment();
      }
    },
    [handleInvoiceWithoutAttachment, handleReceiptWithoutAttachment, isInvoice]
  );

  return (
    <AttachDocumentContext.Provider
      value={{
        dataState,
        setDataState,
        attachmentListData,
        setAttachmentListData,
        isLoading: isAttachDocumentLoading,
        count,
        getAttachDocuments,
        handleAttachment: handleReceiptOrInvoiceAttachment,
        handleWithoutAttachment,
        isRowSelected: !!selectedRow[0]?.selected,
        isInvoice,
        selectedAttachments,
        setSelectedAttachments,
        isExistingDocument: !!receiptId,
        isAttachmentButtonLoading,
      }}
    >
      {children}
    </AttachDocumentContext.Provider>
  );
};

export default AttachDocumentContextProvider;
